import type { Job, JobRun } from '../../types/jobs';
import JobCard from './JobCard';

interface JobKanbanProps {
  jobs: Job[];
  recentRuns: JobRun[];
  onJobClick: (job: Job) => void;
}

type JobCategory = 'recurring' | 'daily' | 'weekly' | 'monthly';

interface Column {
  key: JobCategory;
  label: string;
}

const COLUMNS: Column[] = [
  { key: 'recurring', label: 'Recurring' },
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

export function classifyJob(job: Job): JobCategory {
  if (job.schedule_type === 'interval') return 'recurring';

  const parts = job.schedule_expr.split(/\s+/);
  if (parts.length < 5) return 'daily';

  const [, hour, dom, , dow] = parts;

  // Specific day of month -> monthly
  if (dom !== '*') return 'monthly';

  // Specific day of week -> weekly
  if (dow !== '*') return 'weekly';

  // Runs multiple times daily (e.g., */6) or every day -> daily
  if (hour.includes('/') || hour === '*') return 'daily';

  return 'daily';
}

function getLatestRun(jobId: string, recentRuns: JobRun[]): JobRun | undefined {
  return recentRuns.find((r) => r.job_id === jobId);
}

export default function JobKanban({ jobs, recentRuns, onJobClick }: JobKanbanProps) {
  const grouped: Record<JobCategory, Job[]> = {
    recurring: [],
    daily: [],
    weekly: [],
    monthly: [],
  };

  for (const job of jobs) {
    const cat = classifyJob(job);
    grouped[cat].push(job);
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {COLUMNS.map((col) => (
        <div key={col.key} className="flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-sm font-semibold text-text-primary">{col.label}</h2>
            <span className="text-xs text-text-muted bg-bg-tertiary rounded-full px-2 py-0.5">
              {grouped[col.key].length}
            </span>
          </div>
          {grouped[col.key].map((job) => (
            <JobCard
              key={job.id}
              job={job}
              lastRun={getLatestRun(job.id, recentRuns)}
              onClick={() => onJobClick(job)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
